import React, { useState, useEffect } from 'react';
import { Shield, FileText, Clock, Users, Edit, Save, X, Eye } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  fetchTermosUso,
  saveTermosUso,
  formatDate,
  countWords,
  estimateReadingTime
} from '../services/termosUsoService';
import '../styles/termos-uso.css';

const TermosUso = () => {
  const [termosData, setTermosData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Configuração do editor Quill
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'align',
    'link', 'image', 'color', 'background'
  ];

  // Carregar dados iniciais
  useEffect(() => {
    loadTermosUso();
  }, []);

  const loadTermosUso = async () => {
    try {
      setLoading(true);
      const data = await fetchTermosUso();
      setTermosData(data);
      if (data) {
        setEditContent(data.conteudo);
      }
    } catch (error) {
      console.error('Erro ao carregar termos de uso:', error);
      // Se não existe, criar conteúdo padrão
      setEditContent('<h2>Termos de Uso</h2><p>Digite aqui o conteúdo dos termos de uso...</p>');
    } finally {
      setLoading(false);
    }
  };

  // Iniciar edição
  const startEditing = () => {
    setIsEditing(true);
    setShowPreview(false);
  };

  // Cancelar edição
  const cancelEditing = () => {
    setIsEditing(false);
    setShowPreview(false);
    if (termosData) {
      setEditContent(termosData.conteudo);
    }
  };

  // Salvar alterações
  const handleSave = async () => {
    if (!editContent.trim()) {
      alert('O conteúdo não pode estar vazio.');
      return;
    }

    try {
      setSaving(true);
      const savedData = await saveTermosUso(editContent);
      setTermosData(savedData);
      setIsEditing(false);
      setShowPreview(false);
      alert('Termos de uso salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar termos de uso:', error);
      alert('Erro ao salvar termos de uso. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle preview
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  if (loading) {
    return (
      <div className="termos-uso">
        <div className="page-header">
          <h1 className="page-title">Termos de Uso</h1>
          <p className="page-subtitle">Carregando...</p>
        </div>
      </div>
    );
  }

  const wordCount = termosData ? countWords(termosData.conteudo) : 0;
  const readingTime = termosData ? estimateReadingTime(termosData.conteudo) : 0;

  return (
    <div className="termos-uso">
      <div className="page-header">
        <h1 className="page-title">Termos de Uso</h1>
        <p className="page-subtitle">Políticas e regulamentações da plataforma</p>
        
        <div className="header-actions">
          {!isEditing ? (
            <button className="btn btn-primary" onClick={startEditing}>
              <Edit size={18} />
              Editar Termos
            </button>
          ) : (
            <div className="edit-actions">
              <button 
                className="btn btn-secondary" 
                onClick={togglePreview}
                disabled={saving}
              >
                <Eye size={18} />
                {showPreview ? 'Editar' : 'Visualizar'}
              </button>
              <button 
                className="btn btn-success" 
                onClick={handleSave}
                disabled={saving}
              >
                <Save size={18} />
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
              <button 
                className="btn btn-danger" 
                onClick={cancelEditing}
                disabled={saving}
              >
                <X size={18} />
                Cancelar
              </button>
            </div>
          )}
        </div>
      </div>



      {termosData && (
        <div className="info-card">
          <div className="info-item">
            <strong>Última atualização:</strong> {formatDate(termosData.updated_at)}
          </div>
          <div className="info-item">
            <strong>Criado em:</strong> {formatDate(termosData.created_at)}
          </div>
        </div>
      )}

      <div className="content-card">
        <div className="card-header">
          <h3 className="card-title">
            {isEditing ? 'Editando Termos de Uso' : 'Documento de Termos de Uso'}
          </h3>
        </div>
        
        <div className="card-content">
          {isEditing ? (
            <div className="editor-container">
              {showPreview ? (
                <div className="preview-container">
                  <div className="preview-header">
                    <h4>Visualização</h4>
                  </div>
                  <div 
                    className="terms-content preview-content"
                    dangerouslySetInnerHTML={{ __html: editContent }}
                  />
                </div>
              ) : (
                <div className="quill-container">
                  <ReactQuill
                    theme="snow"
                    value={editContent}
                    onChange={setEditContent}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Digite o conteúdo dos termos de uso..."
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="terms-content">
              {termosData ? (
                <div dangerouslySetInnerHTML={{ __html: termosData.conteudo }} />
              ) : (
                <div className="empty-state">
                  <FileText size={48} />
                  <h3>Nenhum termo de uso encontrado</h3>
                  <p>Clique em "Editar Termos" para criar o documento.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermosUso;
